import React from 'react';
import { ChevronRight, ChevronUp, ChevronDown, CornerDownRight } from 'lucide-react';
import { SortIcon } from './icons';
import SegmentedControl, { SegmentedOption } from './SegmentedControl';

/**
 * The grouped table — "Group By" ledgers with expandable group rows.
 *
 * The pattern the Lobbying and CF explorers established: a Group-by toolbar
 * (SegmentedControl + summary counts), a purple sortable header over a CSS
 * grid (not <table> — group and child rows need free-form layout), group rows
 * that expand in place, and ↳-marked child rows underneath. Those two
 * explorers hand-rolled it independently; this is the shared, presentational
 * extraction. Data fetching, sorting, and expansion state stay in the caller.
 *
 * When to use: ledger-style records with meaningful groupings (transactions
 * by source, filings by client, notes by entity). A list that is only ever
 * flat belongs in StandardTable.
 *
 * Rules and rationale: Documentation/FRONTEND_DESIGN_CONVENTIONS.md.
 * Live example: /design-system. Reference adopter: workspace Notes tab.
 */

/** Grid templates are caller-owned Tailwind classes (`grid-cols-[…]`) shared
 *  by header and rows so columns always line up. */

/**
 * The card the grouped table (and its toolbar) sits in. Same chrome as
 * `.standard-table` — notably its 0.5rem corner radius, NOT `.card`'s
 * rounded-2xl, so grouped and standard tables read as the same family.
 */
export const GroupedTableContainer: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`bg-white dark:bg-background-dark-secondary shadow-sm border border-border-light dark:border-border-dark rounded-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

export interface GroupedTableSort {
  sortBy: string;
  sortDir: 'asc' | 'desc';
  /** Called with the column key and its default direction. */
  onSort: (key: string, defaultDir: 'asc' | 'desc') => void;
}

export function GroupedTableToolbar<T extends string>({
  value,
  onChange,
  options,
  actions,
  summary,
  label = 'Group by',
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  /** Right side, before the summary — quick search, filter/advanced buttons. */
  actions?: React.ReactNode;
  /** Right-aligned counts line, e.g. "55 bills · 1 client". */
  summary?: React.ReactNode;
  label?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 bg-background-tertiary dark:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark">
      <div className="flex items-center gap-2.5">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-accent-500 dark:text-text-dark-tertiary">
          {label}
        </span>
        <SegmentedControl ariaLabel={label} value={value} onChange={onChange} options={options} />
      </div>
      {(actions || summary) && (
        <div className="flex items-center gap-3">
          {actions}
          {summary && (
            <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{summary}</span>
          )}
        </div>
      )}
    </div>
  );
}

export const GroupedTableHeaderCell: React.FC<{
  label: string;
  /** Omit for a non-sortable column. */
  sortKey?: string;
  defaultDir?: 'asc' | 'desc';
  alignRight?: boolean;
  sort?: GroupedTableSort;
}> = ({ label, sortKey, defaultDir = 'asc', alignRight = false, sort }) => {
  if (!sortKey || !sort) {
    return <span className={alignRight ? 'text-right' : undefined}>{label}</span>;
  }
  const active = sort.sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => sort.onSort(sortKey, defaultDir)}
      className={`flex items-center gap-1 uppercase tracking-[0.04em] transition-colors ${alignRight ? 'justify-end' : ''}`}
    >
      <span>{label}</span>
      {active
        ? (sort.sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
        : <SortIcon className="w-3 h-3 opacity-40" />}
    </button>
  );
};

/** The purple header row. `grid` is the shared grid-cols template. */
export const GroupedTableHeader: React.FC<{ grid: string; children: React.ReactNode }> = ({
  grid,
  children,
}) => (
  <div className={`grid ${grid} gap-3.5 px-5 py-2.5 table-header text-[10px] font-semibold tracking-[0.04em] uppercase`}>
    {children}
  </div>
);

/**
 * A group row. Renders the expand chevron + name (both toggle) in one cell;
 * the caller supplies the cells before it (e.g. Latest) and after it
 * (metrics) as children via `before` / `after`.
 */
export const GroupedTableGroupRow: React.FC<{
  grid: string;
  open: boolean;
  onToggle: () => void;
  name: string;
  /** Secondary line under the name (session, type, …). */
  sub?: React.ReactNode;
  before?: React.ReactNode;
  after?: React.ReactNode;
}> = ({ grid, open, onToggle, name, sub, before, after }) => (
  <div className={`grid ${grid} gap-3.5 px-5 py-[11px] border-b border-border-light/60 dark:border-border-dark/50 items-center`}>
    {before}
    <div className="min-w-0 flex items-start gap-1.5">
      <button
        type="button"
        onClick={onToggle}
        className="mt-0.5 text-accent-400 hover:text-primary-800 dark:hover:text-primary-dark-400 transition-colors flex-none"
        aria-label={open ? `Collapse ${name}` : `Expand ${name}`}
      >
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      <div className="min-w-0">
        <button
          type="button"
          onClick={onToggle}
          className="block max-w-full truncate text-left font-semibold text-[12.5px] text-text-primary dark:text-text-dark-primary hover:underline"
          title={name}
        >
          {name}
        </button>
        {sub && <div className="text-[10.5px] text-accent-400 truncate">{sub}</div>}
      </div>
    </div>
    {after}
  </div>
);

/** A single right-aligned numeric metric cell for group rows. */
export const GroupedTableMetricCell: React.FC<{ value: React.ReactNode; primary?: boolean }> = ({
  value,
  primary = false,
}) => (
  <span
    className={primary
      ? 'tabular-nums text-right font-bold text-[13px] text-text-primary dark:text-text-dark-primary'
      : 'tabular-nums text-right text-xs text-text-secondary dark:text-text-dark-secondary'}
  >
    {value}
  </span>
);

/**
 * A mini header labeling the child-row columns, rendered once at the top of
 * an expanded group when child rows have a different shape than group rows
 * (e.g. the Notes tab's author | title | message). Pass the CHILD grid and
 * one cell per column (empty <span/>s for columns that need no label).
 */
export const GroupedTableChildHeader: React.FC<{
  grid: string;
  children: React.ReactNode;
  /** For dynamic templates (e.g. saved-view column widths) that a static Tailwind grid class cannot express. */
  style?: React.CSSProperties;
  /** Extra classes (e.g. a tint distinguishing two nested header levels). */
  className?: string;
}> = ({ grid, children, style, className = '' }) => (
  <div
    className={`${className} grid ${grid} gap-3.5 px-5 py-1.5 border-b border-border-light/60 dark:border-border-dark/50 bg-background-tertiary/60 dark:bg-background-dark-tertiary/40 text-[10px] font-semibold uppercase tracking-[0.04em] text-text-tertiary dark:text-text-dark-tertiary`}
    style={style}
  >
    {children}
  </div>
);

/** A child (or flat-ledger) row under a group. Caller lays out the cells. */
export const GroupedTableRow: React.FC<{
  grid: string;
  children: React.ReactNode;
  className?: string;
  /** For dynamic templates (e.g. saved-view column widths) that a static Tailwind grid class cannot express. */
  style?: React.CSSProperties;
}> = ({ grid, children, className = '', style }) => (
  <div
    className={`grid ${grid} gap-3.5 px-5 py-[9px] border-b border-border-light/60 dark:border-border-dark/50 items-center ${className}`}
    style={style}
  >
    {children}
  </div>
);

/** The ↳ marker that opens every child row's content cell. */
export const ChildRowMarker: React.FC = () => (
  <CornerDownRight className="w-3.5 h-3.5 text-accent-400 flex-none" aria-hidden="true" />
);

/** Inline notice under an expanded group ("Loading records…", "No records"). */
export const GroupedTableNotice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-5 py-2 pl-12 text-xs text-accent-400 border-b border-border-light/60 dark:border-border-dark/50">
    {children}
  </div>
);
